if (!customElements.get('practice-finder')) {
  customElements.define(
    'practice-finder',
    class PracticeFinder extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.form = this.querySelector('[data-practice-finder-form]');
        this.input = this.querySelector('[data-practice-finder-input]');
        this.status = this.querySelector('[data-practice-finder-status]');
        this.results = this.querySelector('[data-practice-finder-results]');
        this.submitButton = this.querySelector('[data-practice-finder-submit]');
        this.locationScript = this.querySelector('[data-practice-finder-locations]');

        if (!this.form || !this.input || !this.status || !this.results) return;

        this.searchRequestId = 0;
        this.locations = this.parseLocations();
        this.form.addEventListener('submit', this.onSubmit.bind(this));
        this.input.addEventListener('input', this.onInput.bind(this));

        const savedZip = window.localStorage.getItem(this.dataset.storageKey);
        if (savedZip) this.input.value = savedZip;
      }

      onInput() {
        if (this.input.value.trim()) return;

        this.searchRequestId += 1;
        this.setLoading(false);
        this.showStatus('');
        this.clearResults();
        window.localStorage.removeItem(this.dataset.storageKey);
      }

      parseLocations() {
        if (!this.locationScript) return [];

        try {
          return JSON.parse(this.locationScript.textContent)
            .map((location) => ({
              name: String(location.name || '').trim(),
              address: String(location.address || '').trim(),
              websiteUrl: String(location.websiteUrl || '').trim(),
              latitude: Number(location.latitude),
              longitude: Number(location.longitude),
            }))
            .filter(
              (location) =>
                location.name &&
                location.address &&
                Number.isFinite(location.latitude) &&
                Number.isFinite(location.longitude)
            );
        } catch (error) {
          return [];
        }
      }

      async onSubmit(event) {
        event.preventDefault();

        const requestId = ++this.searchRequestId;
        const zip = this.input.value.trim();
        if (!/^\d{5}$/.test(zip)) {
          this.setLoading(false);
          this.showStatus(this.dataset.invalidZipText);
          this.clearResults();
          return;
        }

        if (!this.dataset.apiKey) {
          this.setLoading(false);
          this.showStatus(this.dataset.configErrorText);
          this.clearResults();
          return;
        }

        if (!this.locations.length) {
          this.setLoading(false);
          this.showStatus(this.dataset.noLocationsText);
          this.clearResults();
          return;
        }

        this.setLoading(true);
        this.showStatus(this.dataset.loadingText);
        this.clearResults();

        try {
          const origin = await this.geocodeZip(zip);
          if (requestId !== this.searchRequestId) return;

          const matches = this.locations
            .map((location) => ({
              ...location,
              distance: this.distanceInMiles(origin.latitude, origin.longitude, location.latitude, location.longitude),
            }))
            .filter((location) => location.distance <= this.maxDistance)
            .sort((a, b) => a.distance - b.distance)
            .slice(0, this.resultLimit);

          window.localStorage.setItem(this.dataset.storageKey, zip);
          this.renderResults(matches);
        } catch (error) {
          if (requestId !== this.searchRequestId) return;

          this.showStatus(error.name === 'PracticeFinderInvalidZipError' ? this.dataset.invalidZipText : this.dataset.errorText);
          this.clearResults();
        } finally {
          if (requestId === this.searchRequestId) this.setLoading(false);
        }
      }

      async geocodeZip(zip) {
        const url = new URL('https://maps.googleapis.com/maps/api/geocode/json');
        url.searchParams.set('address', zip);
        url.searchParams.set('components', 'country:US|postal_code:' + zip);
        url.searchParams.set('key', this.dataset.apiKey);

        const response = await fetch(url.toString());
        if (!response.ok) throw new Error('Geocoding request failed');

        const data = await response.json();
        if (data.status !== 'OK' || !data.results || !data.results[0]) {
          throw this.createInvalidZipError();
        }

        const location = data.results[0].geometry.location;
        if (!location || !Number.isFinite(Number(location.lat)) || !Number.isFinite(Number(location.lng))) {
          throw this.createInvalidZipError();
        }

        return {
          latitude: Number(location.lat),
          longitude: Number(location.lng),
        };
      }

      createInvalidZipError() {
        const error = new Error('ZIP code could not be geocoded');
        error.name = 'PracticeFinderInvalidZipError';
        return error;
      }

      distanceInMiles(lat1, lon1, lat2, lon2) {
        const radius = 3958.8;
        const dLat = this.toRadians(lat2 - lat1);
        const dLon = this.toRadians(lon2 - lon1);
        const a =
          Math.sin(dLat / 2) * Math.sin(dLat / 2) +
          Math.cos(this.toRadians(lat1)) *
            Math.cos(this.toRadians(lat2)) *
            Math.sin(dLon / 2) *
            Math.sin(dLon / 2);

        return radius * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      }

      toRadians(value) {
        return (value * Math.PI) / 180;
      }

      renderResults(matches, showAll = false) {
        if (!matches.length) {
          this.showStatus(this.dataset.emptyText);
          this.clearResults();
          return;
        }

        this.showStatus('');
        const heading = document.createElement('h3');
        heading.className = 'product-practice-finder__results-heading';
        heading.textContent = this.dataset.resultsHeading;

        const list = document.createElement('ul');
        list.className = 'product-practice-finder__list';

        const visibleMatches = showAll ? matches : matches.slice(0, 1);

        visibleMatches.forEach((location) => {
          const item = document.createElement('li');
          item.className = 'product-practice-finder__item';
          if (location.websiteUrl) item.classList.add('product-practice-finder__item--clickable');

          const content = location.websiteUrl ? document.createElement('a') : document.createElement('div');
          content.className = location.websiteUrl
            ? 'product-practice-finder__item-link'
            : 'product-practice-finder__item-content';

          if (location.websiteUrl) {
            content.href = location.websiteUrl;
            content.target = '_blank';
            content.rel = 'noopener noreferrer';
          }

          const headingRow = document.createElement('div');
          headingRow.className = 'product-practice-finder__item-heading';

          const icon = this.querySelector('[data-practice-finder-icon-template]')?.content.cloneNode(true);
          if (icon) headingRow.append(icon);

          const titleWrap = document.createElement('div');
          const name = document.createElement('h4');
          name.className = 'product-practice-finder__name';
          name.textContent = location.name;

          const distance = document.createElement('p');
          distance.className = 'product-practice-finder__distance';
          distance.textContent = `${Math.round(location.distance)} mi`;

          titleWrap.append(name, distance);
          headingRow.append(titleWrap);

          const address = document.createElement('p');
          address.className = 'product-practice-finder__address';
          address.textContent = location.address;

          content.append(headingRow, address);

          if (location.websiteUrl) {
            const link = document.createElement('span');
            link.className = 'product-practice-finder__link';
            link.textContent = this.dataset.websiteLabel;
            content.append(link);
          }

          item.append(content);
          list.append(item);
        });

        if (!showAll && matches.length > 1) {
          const showAllButton = document.createElement('button');
          showAllButton.className = 'product-practice-finder__show-all';
          showAllButton.type = 'button';
          showAllButton.textContent = this.dataset.showAllLabel;
          showAllButton.addEventListener('click', () => {
            this.renderResults(matches, true);
          });

          this.results.replaceChildren(heading, list, showAllButton);
          return;
        }

        this.results.replaceChildren(heading, list);
      }

      setLoading(isLoading) {
        this.classList.toggle('is-loading', isLoading);
        if (this.submitButton) this.submitButton.disabled = isLoading;
      }

      showStatus(message) {
        this.status.textContent = message || '';
      }

      clearResults() {
        this.results.replaceChildren();
      }

      get resultLimit() {
        return Math.max(1, Number(this.dataset.resultLimit) || 5);
      }

      get maxDistance() {
        return Math.max(0, Number(this.dataset.maxDistance) || 100);
      }
    }
  );
}
